import type {
  AxisCalibrationRunView,
  AxisCalibrationSummary,
  AxisSampleExport,
  ImportAxisSamplesResult,
} from "@shared/labs/level-assessment-calibration";
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { ActiveModelProvider, useActiveModel } from "../active-model-context";
import { ModelPicker } from "../model-picker";

/**
 * Calibration command center.
 *
 * Per-axis view of the calibration corpus: how many ground-truth samples each
 * axis has (split into confirmations vs. corrections), when the last sample
 * landed, when the axis was last calibrated, and a per-axis trigger for the
 * optimization procedure. Samples are collected from the end-of-evaluation
 * review flow; optimization runs at the axis grain, so everything here is
 * keyed by axis.
 */
export default function LevelAssessmentCalibration() {
  return (
    <ActiveModelProvider>
      <CommandCenter />
    </ActiveModelProvider>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

function transferBtnStyle(busy: boolean): CSSProperties {
  return {
    fontSize: 12.5,
    fontWeight: 600,
    padding: "7px 13px",
    borderRadius: 999,
    cursor: busy ? "not-allowed" : "pointer",
    background: "#fff",
    color: "var(--r-ink)",
    border: "1px solid var(--r-ink-faint, #e9e3d8)",
    opacity: busy ? 0.7 : 1,
  };
}

function CommandCenter() {
  const { modelId } = useActiveModel();
  const { toast } = useToast();
  const [summary, setSummary] = useState<AxisCalibrationSummary[] | null>(null);
  const [runs, setRuns] = useState<AxisCalibrationRunView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, r] = await Promise.all([
        rpcClient.labs.levelAssessmentAxisSampleSummary({ modelId }) as Promise<{
          axes: AxisCalibrationSummary[];
        }>,
        rpcClient.labs.levelAssessmentAxisCalibrationRunsList({ modelId }) as Promise<{
          runs: AxisCalibrationRunView[];
        }>,
      ]);
      setSummary(s.axes);
      setRuns(r.runs);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro inesperado");
    }
  }, [modelId]);

  // ── Export / import the model's sample corpus (move between databases) ──
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [transfer, setTransfer] = useState<"export" | "import" | null>(null);
  const [replaceMode, setReplaceMode] = useState(false);

  const onExport = useCallback(async () => {
    setTransfer("export");
    try {
      const data = (await rpcClient.labs.levelAssessmentAxisSamplesExport({
        modelId,
      })) as AxisSampleExport;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `axis-samples-${modelId}-${data.exportedAt.slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Amostras exportadas", description: `${data.count} amostras baixadas.` });
    } catch (e) {
      toast({
        title: "Falha ao exportar",
        description: e instanceof Error ? e.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setTransfer(null);
    }
  }, [modelId, toast]);

  const onImportFile = useCallback(
    async (file: File) => {
      setTransfer("import");
      try {
        const payload = JSON.parse(await file.text());
        const res = (await rpcClient.labs.levelAssessmentAxisSamplesImport({
          modelId,
          mode: replaceMode ? "replace" : "skip_existing",
          payload,
        })) as ImportAxisSamplesResult;
        toast({
          title: "Amostras importadas",
          description: `${res.imported} importadas · ${res.skipped} já existiam · ${res.invalid} inválidas (de ${res.total}).`,
        });
        await load();
      } catch (e) {
        toast({
          title: "Falha ao importar",
          description: e instanceof Error ? e.message : "Arquivo inválido",
          variant: "destructive",
        });
      } finally {
        setTransfer(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [modelId, replaceMode, toast, load],
  );

  useEffect(() => {
    setSummary(null);
    void load();
  }, [load]);

  /** Latest run (any status) per axis identity, for the inline report. */
  const latestRunByKey = useMemo(() => {
    const m = new Map<string, AxisCalibrationRunView>();
    for (const run of runs) {
      const k = `${run.scope}:${run.disciplineId ?? ""}:${run.axisId}`;
      if (!m.has(k)) m.set(k, run); // runs come newest-first
    }
    return m;
  }, [runs]);

  const onCalibrate = useCallback(
    async (axis: AxisCalibrationSummary) => {
      const key = `${axis.scope}:${axis.disciplineId ?? ""}:${axis.axisId}`;
      setTriggering(key);
      try {
        await rpcClient.labs.levelAssessmentAxisCalibrationTrigger({
          modelId,
          disciplineId: axis.disciplineId,
          axisId: axis.axisId,
          scope: axis.scope,
        });
        toast({ title: "Calibração disparada", description: `Eixo "${axis.label}".` });
        await load();
      } catch (e) {
        toast({
          title: "Falha ao calibrar",
          description: e instanceof Error ? e.message : "Erro inesperado",
          variant: "destructive",
        });
      } finally {
        setTriggering(null);
      }
    },
    [modelId, load, toast],
  );

  const [accepting, setAccepting] = useState<string | null>(null);
  const onAccept = useCallback(
    async (run: AxisCalibrationRunView) => {
      setAccepting(run.id);
      try {
        await rpcClient.labs.levelAssessmentAxisCalibrationAccept({ runId: run.id });
        toast({ title: "Prompt aplicado", description: `Eixo "${run.axisId}".` });
        await load();
      } catch (e) {
        toast({
          title: "Falha ao aplicar prompt",
          description: e instanceof Error ? e.message : "Erro inesperado",
          variant: "destructive",
        });
      } finally {
        setAccepting(null);
      }
    },
    [load, toast],
  );

  // GEPA runs in the background — poll while any run is still running.
  const anyRunning = useMemo(
    () => [...latestRunByKey.values()].some((r) => r.status === "running"),
    [latestRunByKey],
  );
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(() => void load(), 4000);
    return () => clearInterval(t);
  }, [anyRunning, load]);

  const cross = (summary ?? []).filter((a) => a.scope === "cross");
  const verticalByDiscipline = useMemo(() => {
    const groups = new Map<string, AxisCalibrationSummary[]>();
    for (const a of summary ?? []) {
      if (a.scope !== "vertical" || !a.disciplineId) continue;
      const arr = groups.get(a.disciplineId) ?? [];
      arr.push(a);
      groups.set(a.disciplineId, arr);
    }
    return groups;
  }, [summary]);

  return (
    <div className="ci-stripe ci-stripe-page">
      <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 1100 }}>
        <div style={{ marginBottom: 20 }}>
          <Link
            href="/labs/level-assessment"
            style={{
              fontSize: 12,
              color: "var(--r-ink-muted)",
              textDecoration: "none",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            ← Voltar para avaliação
          </Link>
          <h1
            style={{
              margin: "8px 0 6px",
              fontSize: 28,
              fontWeight: 600,
              color: "var(--r-ink)",
              letterSpacing: "-0.01em",
            }}
          >
            Calibração
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: "var(--r-ink-muted)",
              lineHeight: 1.55,
              maxWidth: 760,
            }}
          >
            Centro de comando da calibração. As amostras são coletadas ao revisar cada avaliação —
            cada uma é um par (perfil × eixo) com o nível corrigido. A otimização roda por eixo.
          </p>
          <Link
            href="/labs/level-assessment/calibration-data"
            data-testid="calibration-data-link"
            style={{
              display: "inline-block",
              marginTop: 8,
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--r-ink)",
              textDecoration: "underline",
              textDecorationStyle: "dotted",
              textUnderlineOffset: 3,
            }}
          >
            Ver análise dos dados →
          </Link>
        </div>

        <ModelPicker />

        {/* Move this model's corpus between databases (e.g. local → produção). */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <button
            type="button"
            data-testid="axis-samples-export"
            disabled={transfer !== null}
            onClick={onExport}
            style={transferBtnStyle(transfer === "export")}
          >
            {transfer === "export" ? "Exportando…" : "Exportar amostras"}
          </button>
          <button
            type="button"
            data-testid="axis-samples-import"
            disabled={transfer !== null}
            onClick={() => fileInputRef.current?.click()}
            style={transferBtnStyle(transfer === "import")}
          >
            {transfer === "import" ? "Importando…" : "Importar amostras"}
          </button>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12.5,
              color: "var(--r-ink-muted)",
              cursor: "pointer",
            }}
            title="Sobrescreve amostras já existentes para o mesmo (perfil × eixo). Desligado: só importa o que falta."
          >
            <input
              type="checkbox"
              checked={replaceMode}
              onChange={(e) => setReplaceMode(e.target.checked)}
            />
            substituir existentes
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onImportFile(f);
            }}
          />
        </div>

        {error && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              background: "var(--r-coral-soft, #fdecee)",
              color: "var(--r-coral, #f3354b)",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            Falha ao carregar: {error}
          </div>
        )}

        {summary === null ? (
          <div style={{ fontSize: 13, color: "var(--r-ink-muted)", padding: "24px 0" }}>
            Carregando resumo de calibração…
          </div>
        ) : (
          <>
            <AxisGroup
              title="Competências cross"
              subtitle="Pontuadas para todo candidato."
              axes={cross}
              latestRunByKey={latestRunByKey}
              triggering={triggering}
              accepting={accepting}
              onCalibrate={onCalibrate}
              onAccept={onAccept}
            />
            {[...verticalByDiscipline.entries()].map(([disciplineId, axes]) => (
              <AxisGroup
                key={disciplineId}
                title={`Competências verticais · ${axes[0]?.label.split(" · ")[0] ?? disciplineId}`}
                subtitle="Pontuadas apenas quando a avaliação mira esta disciplina."
                axes={axes}
                latestRunByKey={latestRunByKey}
                triggering={triggering}
                accepting={accepting}
                onCalibrate={onCalibrate}
                onAccept={onAccept}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function AxisGroup({
  title,
  subtitle,
  axes,
  latestRunByKey,
  triggering,
  accepting,
  onCalibrate,
  onAccept,
}: {
  title: string;
  subtitle: string;
  axes: AxisCalibrationSummary[];
  latestRunByKey: Map<string, AxisCalibrationRunView>;
  triggering: string | null;
  accepting: string | null;
  onCalibrate: (axis: AxisCalibrationSummary) => void;
  onAccept: (run: AxisCalibrationRunView) => void;
}) {
  if (axes.length === 0) return null;
  return (
    <section style={{ marginBottom: 28 }}>
      <div style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--r-ink)" }}>{title}</h2>
        <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "var(--r-ink-muted)" }}>{subtitle}</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {axes.map((axis) => {
          const key = `${axis.scope}:${axis.disciplineId ?? ""}:${axis.axisId}`;
          const latestRun = latestRunByKey.get(key);
          const axisLabel = axis.label.includes(" · ")
            ? axis.label.split(" · ").slice(1).join(" · ")
            : axis.label;
          const isTriggering = triggering === key;
          const noSamples = axis.sampleCount === 0;
          return (
            <div
              key={key}
              data-testid={`calibration-axis-${axis.axisId}`}
              className="card"
              style={{
                padding: "14px 16px",
                display: "flex",
                alignItems: "center",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--r-ink)" }}>
                  {axisLabel}
                </div>
                <div style={{ fontSize: 12, color: "var(--r-ink-muted)", marginTop: 2 }}>
                  Último sample: {fmtDate(axis.lastSampleAt)} · Última calibração:{" "}
                  {fmtDate(axis.lastCalibratedAt)}
                  {latestRun ? ` (${latestRun.status})` : ""}
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <SampleBadge
                  count={axis.sampleCount}
                  agreed={axis.agreedCount}
                  disagreed={axis.disagreedCount}
                />
                <button
                  type="button"
                  data-testid={`calibration-trigger-${axis.axisId}`}
                  disabled={noSamples || isTriggering}
                  onClick={() => onCalibrate(axis)}
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "8px 14px",
                    borderRadius: 999,
                    cursor: noSamples || isTriggering ? "not-allowed" : "pointer",
                    background: noSamples ? "var(--r-sand, #f4f1ea)" : "var(--r-ink)",
                    color: noSamples ? "var(--r-ink-muted)" : "#fff",
                    border: "none",
                    opacity: isTriggering ? 0.7 : 1,
                  }}
                  title={
                    noSamples ? "Sem amostras coletadas para este eixo" : "Disparar otimização"
                  }
                >
                  {isTriggering ? "Calibrando…" : "Calibrar"}
                </button>
              </div>

              {latestRun && (
                <RunReport
                  run={latestRun}
                  accepting={accepting === latestRun.id}
                  onAccept={() => onAccept(latestRun)}
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SampleBadge({
  count,
  agreed,
  disagreed,
}: {
  count: number;
  agreed: number;
  disagreed: number;
}) {
  return (
    <div
      style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", minWidth: 96 }}
      title={`${agreed} confirmações · ${disagreed} correções`}
    >
      <div style={{ fontSize: 18, fontWeight: 700, color: "var(--r-ink)", lineHeight: 1.1 }}>
        {count}
      </div>
      <div style={{ fontSize: 11, color: "var(--r-ink-muted)" }}>
        {count === 1 ? "amostra" : "amostras"} · {agreed}✓ / {disagreed}✎
      </div>
    </div>
  );
}

/** Loose shape of the GEPA accuracy report (server owns the canonical type). */
interface GepaReportShape {
  status?: string;
  note?: string;
  rolloutsUsed?: number;
  rolloutBudget?: number;
  candidatesProposed?: number;
  candidatesAccepted?: number;
  paretoSetSize?: number;
  feedbackSetSize?: number;
  baselineParetoMean?: number;
  bestParetoMean?: number;
  baselineExactMatch?: number;
  bestExactMatch?: number;
  routingChanges?: { added?: string[]; dropped?: string[]; retiered?: string[] };
}

function pct(x?: number): string {
  return typeof x === "number" ? `${Math.round(x * 100)}%` : "—";
}

function RunReport({
  run,
  accepting,
  onAccept,
}: {
  run: AxisCalibrationRunView;
  accepting: boolean;
  onAccept: () => void;
}) {
  const report = (run.accuracyReport ?? {}) as GepaReportShape;

  if (run.status === "running") {
    return (
      <div style={{ flexBasis: "100%", fontSize: 12.5, color: "var(--r-ink-muted)" }}>
        Calibrando com GEPA… (em segundo plano — esta tela atualiza sozinha)
      </div>
    );
  }
  if (run.status === "error") {
    return (
      <div style={{ flexBasis: "100%", fontSize: 12.5, color: "var(--r-coral, #f3354b)" }}>
        Falha na calibração: {run.errorMessage ?? "erro desconhecido"}
      </div>
    );
  }
  if (report.status === "skipped") {
    return (
      <div style={{ flexBasis: "100%", fontSize: 12.5, color: "var(--r-amber, #b07811)" }}>
        {report.note ?? "Corrida ignorada."}
      </div>
    );
  }

  return (
    <div
      style={{ flexBasis: "100%", marginTop: 4, display: "flex", flexDirection: "column", gap: 8 }}
    >
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 12.5 }}>
        <Stat
          label="Acerto exato"
          value={`${pct(report.baselineExactMatch)} → ${pct(report.bestExactMatch)}`}
        />
        <Stat
          label="Score médio"
          value={`${(report.baselineParetoMean ?? 0).toFixed(2)} → ${(report.bestParetoMean ?? 0).toFixed(2)}`}
        />
        <Stat
          label="Candidatos"
          value={`${report.candidatesAccepted ?? 0} aceitos / ${report.candidatesProposed ?? 0} propostos`}
        />
        <Stat
          label="Rollouts"
          value={`${report.rolloutsUsed ?? 0} / ${report.rolloutBudget ?? 0}`}
        />
      </div>

      {(() => {
        const rc = report.routingChanges;
        const parts = [
          ...(rc?.added ?? []).map((s) => `+${s}`),
          ...(rc?.retiered ?? []).map((s) => `~${s}`),
          ...(rc?.dropped ?? []).map((s) => `−${s}`),
        ];
        if (parts.length === 0) return null;
        return (
          <div style={{ fontSize: 12, color: "var(--r-ink-muted)" }}>
            <span style={{ fontWeight: 600, color: "var(--r-ink)" }}>Roteamento proposto: </span>
            {parts.join(", ")}
          </div>
        );
      })()}

      {run.accepted ? (
        <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--r-green, #1b6148)" }}>
          ✓ Prompt {report.routingChanges ? "e roteamento " : ""}aplicados a este eixo.
        </div>
      ) : run.proposedPrompt ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            data-testid={`calibration-accept-${run.axisId}`}
            disabled={accepting}
            onClick={onAccept}
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              padding: "7px 14px",
              borderRadius: 999,
              cursor: accepting ? "not-allowed" : "pointer",
              background: "var(--r-green, #1b6148)",
              color: "#fff",
              border: "none",
              opacity: accepting ? 0.7 : 1,
            }}
          >
            {accepting
              ? "Aplicando…"
              : report.routingChanges
                ? "Aceitar prompt + roteamento"
                : "Aceitar novo prompt"}
          </button>
          <details>
            <summary style={{ fontSize: 12, color: "var(--r-ink-muted)", cursor: "pointer" }}>
              Ver prompt proposto
            </summary>
            <pre
              style={{
                margin: "8px 0 0",
                padding: "10px 12px",
                background: "var(--r-bg-soft, #fff8ec)",
                borderRadius: 8,
                fontSize: 11.5,
                lineHeight: 1.5,
                maxHeight: 360,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                color: "var(--r-ink)",
              }}
            >
              {run.proposedPrompt}
            </pre>
          </details>
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: "var(--r-ink-muted)" }}>
          Nenhuma melhora sobre o prompt atual — nada a aplicar.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ fontVariantNumeric: "tabular-nums" }}>
      <span style={{ color: "var(--r-ink-muted)" }}>{label}: </span>
      <span style={{ fontWeight: 600, color: "var(--r-ink)" }}>{value}</span>
    </div>
  );
}
